import { useT } from '../../i18n/index.ts';
import { Panel, RoutePath, type RouteNode } from '../../ui/index.ts';

/**
 * The candidate ladder, on the page (#1916, and the direction #1484 asks for).
 *
 * Operators reason about this page as "a list of configs", which leaves the
 * actual question — given a request, WHICH of these decides it — to be
 * reconstructed from memory every time. It is four tiers, and two of them are
 * routinely confused:
 *
 *   - an `inherit` config falls through to the GLOBAL DEFAULT CONFIG, not to
 *     the platform default tables. Those tables are one tier further down and
 *     are unreachable while the global default is `exclusive`, which it is.
 *   - an org-level assignment beats the global default, so a global switch
 *     does not move an org that carries its own assignment.
 *
 * Rendered as a route strip rather than prose because the ordering IS the
 * content, and the lamp tones carry the "which of these is unusual" reading:
 * the override tier is only lit during an incident.
 */
export function RoutingLadderNote(): JSX.Element {
  const t = useT();
  // Tones carry the reading, not decoration: the override tier is lit `warn`
  // because it is only ever engaged during an incident; the global default is
  // `ok` because it is where most traffic legitimately lands; the platform
  // default tables are `off` because with an `exclusive` global default that
  // tier is currently unreachable.
  const nodes: readonly RouteNode[] = [
    { label: t('tenant_routing.ladder.tier_override'), tone: 'warn' },
    { label: t('tenant_routing.ladder.tier_org'), tone: 'signal' },
    { label: t('tenant_routing.ladder.tier_global'), tone: 'ok' },
    { label: t('tenant_routing.ladder.tier_default_tables'), tone: 'off' },
  ];
  return (
    <Panel caption={t('tenant_routing.ladder.title')} testId="tenant-routing-ladder-note" padded>
      <div className="flex flex-col gap-2">
        <RoutePath nodes={nodes} testId="tenant-routing-ladder-path" />
        <p className="m-0 text-dense leading-5 text-paper-low">
          {t('tenant_routing.ladder.body')}
        </p>
      </div>
    </Panel>
  );
}
